import { useEffect, useState } from 'react';
import { Button, Image, Modal, Space, Spin, Table, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExclamationCircleFilled, EyeOutlined } from '@ant-design/icons';
import PayTypeModal from './payTypeModal';
import {
  deletePayTypeApi,
  getPayTypeListApi,
  getPayTypeSupportApi,
  updatePayTypeStatusApi,
} from '@/services/signAndPayment';
import './index.less';

const { confirm } = Modal;

interface DataType {
  type: string;
  name: string;
  category: number;
  ways: string[];
  description: string;
  mchMode: boolean;
  ispMode: boolean;
  icon: string;
  iconUrl?: string;
  status: boolean;
  createdAt?: string;
}
interface PayWay {
  name: string;
  code: string;
}
interface PayTypeSupport {
  category: number;
  categoryName: string;
  type: string;
  typeName: string;
  ways: PayWay[];
}

const PaymentTypeList = () => {
  const [loading, setLoading] = useState(true);
  const [editType, setEditType] = useState<number>(0);
  const [payTypeList, setPayTypeList] = useState<DataType[]>([]);
  const [payTypeSupported, setPayTypeSupported] = useState<PayTypeSupport[]>([]);
  const [editVisible, setEditVisible] = useState(false);
  const [selectedPayType, setSelectedPayType] = useState({});

  const getPayTypeList = async () => {
    const res = await getPayTypeListApi();
    if (res.data && res.data.code === 'SUCCESS' && res.code === 200) {
      setPayTypeList(res.data.details);
    }
    setLoading(false);
  };
  const getPayWays = async () => {
    const res = await getPayTypeSupportApi();
    if (res.data && res.data.code === 'SUCCESS' && res.code === 200) {
      setPayTypeSupported(res.data.details);
    }
  };

  useEffect(() => {
    getPayWays();
    getPayTypeList();
  }, []);

  // 更新支付类型状态
  const updatePayTypeStatus = async (type: string, status: number) => {
    const res = await updatePayTypeStatusApi(type, status);
    if (res.data && res.data.code === 'SUCCESS' && res.code === 200) {
      message.info(status === 1 ? '启用成功' : '禁用成功');
      getPayTypeList();
    }
  };

  const updateSignStatus = (record: DataType) => {
    if (!record.status) {
      // 启用支付类型
      updatePayTypeStatus(record.type, 1);
    } else {
      confirm({
        icon: <ExclamationCircleFilled />,
        title: `确定要禁用【${record.name}】支付类型?`,
        content: '禁用后客户将无法使用该支付类型进行订单支付',
        okText: '确定',
        onOk: () => {
          // 禁用支付类型
          updatePayTypeStatus(record.type, 0);
        },
      });
    }
  };

  const editPayType = (type: number, record?: any) => {
    setEditType(type);
    setSelectedPayType(record);
    setEditVisible(true);
  };

  const handleCancel = () => {
    setEditVisible(false);
    getPayTypeList();
  };

  const deletePayType = async (record: any) => {
    confirm({
      icon: <ExclamationCircleFilled />,
      title: `确定要删除【${record.name}】支付类型?`,
      okText: '确定',
      onOk: async () => {
        const res = await deletePayTypeApi(record.type);
        if (res.data && res.data.code === 'SUCCESS' && res.code === 200) {
          message.info('删除成功');
          getPayTypeList();
        }
      },
    });
  };

  const getPayWayName = (type: string, way: string) => {
    const ways = payTypeSupported.find((item) => item.type === type)?.ways;
    if (ways && ways.length > 0) {
      return ways.find((item: any) => item.code === way)?.name;
    }
    return way;
  };

  const columns: ColumnsType<DataType> = [
    {
      title: '支付类型名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      fixed: 'left',
      render: (text) => <span>{text}</span>,
    },
    {
      title: '支付类型标识',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      fixed: 'left',
      render: (text) => <span>{text}</span>,
    },
    {
      title: '支付类型图标',
      dataIndex: 'iconUrl',
      key: 'iconUrl',
      width: 150,
      render: (text) =>
        text && (
          <div className="iconFile" style={{ width: `50px`, height: `50px` }}>
            <Image preview={{ mask: <EyeOutlined /> }} height={50} src={text} />
          </div>
        ),
    },
    {
      title: '是否在线支付',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (text) => <span>{text === 0 ? '在线支付' : '非在线支付'}</span>,
    },
    {
      title: '支付方式',
      dataIndex: 'ways',
      key: 'ways',
      width: 150,
      render: (text, record) =>
        text.map((way: string, index: number) => {
          return (
            <span
              className="payWay"
              style={{ marginLeft: `${index === 0 ? '0' : '5px'}`, marginTop: '5px' }}
            >
              {getPayWayName(record.type, way)}
            </span>
          );
        }),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 150,
      render: (text) => <span>{text}</span>,
    },
    {
      title: '商户模式',
      dataIndex: 'mchMode',
      key: 'mchMode',
      width: 150,
      render: (text) => <span>{text ? '支持' : '不支持'}</span>,
    },
    {
      title: '服务商模式',
      dataIndex: 'ispMode',
      key: 'ispMode',
      width: 150,
      render: (text) => <span>{text ? '支持' : '不支持'}</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (text) => (
        <span className={text ? 'statusEnabled' : 'statusDisabled'}>{text ? '启用' : '禁用'}</span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <div
            className="actionBtn"
            style={{ color: record.status ? 'red' : '' }}
            onClick={() => updateSignStatus(record)}
          >
            {record.status ? '禁用' : '启用'}
          </div>
          <div className="actionBtn" onClick={() => editPayType(1, record)}>
            编辑
          </div>
          <div className="actionBtn" style={{ color: 'red' }} onClick={() => deletePayType(record)}>
            删除
          </div>
        </Space>
      ),
    },
  ];

  return (
    <div className="payTypeBody">
      <div className="payTypeHead">
        <Button type="primary" onClick={() => editPayType(0, {})}>
          新增
        </Button>
      </div>
      <Spin spinning={loading}>
        <div className="payTypeContent">
          <Table
            columns={columns}
            dataSource={payTypeList}
            rowKey={(record: any) => record.type}
            pagination={false}
            scroll={{ x: 1500, y: 'calc(100vh - 340px)' }}
          />
        </div>
      </Spin>
      {editVisible && (
        <PayTypeModal
          data={selectedPayType}
          payTypeSupported={payTypeSupported}
          visible={editVisible}
          editType={editType}
          handleCancel={handleCancel}
        />
      )}
    </div>
  );
};
export default PaymentTypeList;
